import { useState } from 'react';
import { Expand } from 'lucide-react';

import { cn, Modal, ModalContent } from '@sandtech-hub-monorepo/ui-kit';
import { CertificateTemplatePreview } from '@sandtech-hub-monorepo/ui-kit-site';

import { CatalogueProgram } from '../../../data/programs';

interface Props {
  program: CatalogueProgram;
  /** Whether a covering subscription (or the free tier) already unlocks this
   *  program, same signal the enroll button and activity paywalls read. The
   *  template preview greys out to read as locked for everyone else; a
   *  learner who can actually work towards earning it sees it in colour,
   *  even before they've earned it. */
  covered?: boolean;
}

/**
 * The program certificate, mirroring the ALX program page (central-hub
 * EnrollmentSidebar): the issued certificate once earned, otherwise the
 * definition's template, with the name and dates left blank so it never
 * reads as already awarded. Renders nothing when there is neither.
 */
export const ProgramCredentials = ({ program, covered = false }: Props) => {
  const [open, setOpen] = useState(false);

  const certificateDefinition = program.certificateDefinition;
  const earnedImageUrl = program.issuedCertificate?.imageUrl ?? null;
  const hasTemplate = !!(
    certificateDefinition?.templateConfig as { html?: string } | null
  )?.html;

  const certificate = earnedImageUrl ? (
    <img
      src={earnedImageUrl}
      alt={`Certificate for ${program.name}`}
      className="h-auto w-full"
      width={1280}
      height={720}
    />
  ) : certificateDefinition && hasTemplate ? (
    // text-left: the template inherits the button's centred text otherwise.
    <div className={cn('text-left', !covered && 'grayscale')}>
      <CertificateTemplatePreview
        templateConfig={certificateDefinition.templateConfig}
        programName={program.name}
        earningCriteria={certificateDefinition.criteriaText}
        // A blank that keeps the name line's height, so the layout holds.
        learnerName={'\u00A0'}
        dateLabel=""
      />
    </div>
  ) : null;

  if (!certificate) return null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-heading">Certificate</h3>
        <span className="text-xs uppercase tracking-wider text-muted-foreground">
          {earnedImageUrl ? 'Earned' : 'Preview'}
        </span>
      </div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="View certificate larger"
        className="relative block w-full overflow-hidden rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {certificate}
        <span className="pointer-events-none absolute right-2 top-2 rounded-md bg-background/90 p-1.5 text-foreground shadow-sm">
          <Expand className="h-3 w-3" />
        </span>
      </button>

      <Modal open={open} onOpenChange={setOpen}>
        <ModalContent className="max-w-none p-6 md:w-[min(96vw,1100px)]">
          {certificate}
        </ModalContent>
      </Modal>
    </div>
  );
};
